import { GoFeatureFlagWebProvider } from "@openfeature/go-feature-flag-web-provider";
import { type Client, type EvaluationContext, OpenFeature, ProviderEvents } from "@openfeature/web-sdk";
import { ref } from "vue";

import config from "../config";
import { auth } from "../keycloak";
import { router } from "../router";
import { reportSilently } from "../util/reportError";
import { getCurrentRegion } from "../util/route";
import { type FeatureFlagClaims, buildEvaluationContext } from "./context";

export { buildEvaluationContext, regionToFlagRegion } from "./context";

// Bumped whenever the OpenFeature provider cache changes (initial fetch
// finishing, websocket pushes, context-driven re-evaluation). useFeatureFlag's
// computed reads this so gated UI re-renders instead of being stuck on its
// first synchronous evaluation. See ADR feature-flags-openfeature-goff.
export const flagVersion = ref(0);

// The OpenFeature client, set once the provider is ready. Left undefined when
// initialisation is skipped or fails so flags fail closed (resolve to false).
let openFeatureClient: Client | undefined;

export const getOpenFeatureClient = (): Client | undefined => openFeatureClient;

/**
 * Builds the evaluation context from the current Keycloak token and route
 * region. Read at call time (not cached) so each refresh sees the latest token.
 */
export const buildContext = (): EvaluationContext =>
  buildEvaluationContext(
    (auth.accessTokenParsed ?? {}) as FeatureFlagClaims,
    getCurrentRegion(router.currentRoute.value),
    config.featureFlags.environment,
  );

/** Pushes a freshly-built context to OpenFeature. Safe to call before init. */
export const refreshContext = async (): Promise<void> => {
  if (!openFeatureClient) {
    return;
  }
  try {
    await OpenFeature.setContext(buildContext());
  } catch (err) {
    // setContext can reject if the provider rejects the context. Swallow it
    // (silent to the user): evaluations keep returning their default (false)
    // until the next refresh. Report to Sentry for observability.
    reportSilently(err, { feature: "feature-flags", op: "setContext" });
  }
};

/**
 * Initialises the GO Feature Flag provider and wires reactivity. Fails closed:
 * if the endpoint is missing or the provider fails to initialise, the client is
 * left undefined and every flag resolves to its default (false).
 *
 * Must be called after auth.init() resolves so the access token is available.
 */
export const initFeatureFlags = async (): Promise<void> => {
  const endpoint = config.featureFlags.endpoint;
  if (!endpoint) {
    return;
  }

  // Wrap everything: this runs fire-and-forget (`void initFeatureFlags()`), so
  // any throw would become an unhandled rejection. Failing closed (no client →
  // flags resolve to false) is silent to the user; we report to Sentry instead.
  try {
    const provider = new GoFeatureFlagWebProvider({ endpoint });
    await OpenFeature.setContext(buildContext());
    await OpenFeature.setProviderAndWait(provider);

    openFeatureClient = OpenFeature.getClient();

    const bump = () => {
      flagVersion.value++;
    };
    for (const event of [
      ProviderEvents.Ready,
      ProviderEvents.ConfigurationChanged,
      ProviderEvents.ContextChanged,
      ProviderEvents.Stale,
      ProviderEvents.Error,
    ]) {
      openFeatureClient.addHandler(event, bump);
    }

    // The route region changes as the user navigates between EU/US services;
    // keep the context in sync. (Token-change refresh is wired in index.ts,
    // which owns auth.onAccessTokenChange — see refreshContext usage there.)
    router.afterEach((to, from) => {
      // Context only depends on the region param; skip the relay round-trip on
      // navigations that don't change it.
      if (getCurrentRegion(to) === getCurrentRegion(from)) {
        return;
      }
      void refreshContext();
    });
  } catch (err) {
    reportSilently(err, { feature: "feature-flags", op: "init" });
  }
};
